import { Delete, ShieldAlert } from 'lucide-react'
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react'

import { Button } from './Button'
import './codeEntry.css'

export interface CodeEntryProps {
  /** Подпись поля, например «Код кассира». */
  label: string
  /** Подсказка под полем; по умолчанию — про сканер штрихкода. */
  hint?: ReactNode
  /** Надпись главной кнопки и запись реестра действий для её ⓘ. */
  submitLabel?: string
  submitActionId?: string
  /** Код проверяется: ввод закрыт, кнопка занята. */
  busy?: boolean
  /** Ввод закрыт снаружи (кассир не выбран, блокировка). */
  disabled?: boolean
  /** Ошибка проверки: сразу под полем, пока человек не начнёт вводить код заново. Слова сервера — дословно. */
  error?: ReactNode | null
  /** Меньше цифр отправить нельзя. */
  minLength?: number
  maxLength?: number
  /** Код уходит наружу один раз; в поле он не остаётся. */
  onSubmit: (code: string) => void
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const

/**
 * Ввод кода (PIN, штрихкод бейджа: цифры и латинские буквы) как на смартфоне: фокус сразу в поле, сканер штрихкода печатает как клавиатура и
 * завершает Enter, цифровая панель — для касания и мыши (экранная клавиатура телефона при этом не выскакивает). Только цифры; код
 * показывается точками и стирается сразу при отправке — нигде не хранится. Ошибка стоит под полем и объявляется диктору.
 */
export function CodeEntry({ label, hint, submitLabel = 'Войти', submitActionId, busy = false, disabled = false, error = null, minLength = 4, maxLength = 32, onSubmit }: CodeEntryProps) {
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  const input = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState('')
  const off = disabled || busy
  const hasError = error !== null && error !== undefined && error !== false && !busy
  useEffect(() => { if (!off) input.current?.focus() }, [off, hasError])

  const change = (next: string) => setValue(next.replace(/[^0-9A-Za-z]/g, '').slice(0, maxLength))
  const submit = (event?: FormEvent) => {
    event?.preventDefault()
    if (off || value.length < minLength) return
    const code = value
    setValue('')
    onSubmit(code)
  }
  const press = (key: string) => { if (!off) change(value + key) }

  return (
    <form className="krp-code" onSubmit={submit} aria-label={`Ввод кода: ${label}`}>
      <label className="krp-code__label" htmlFor={id}>{label}</label>
      <input
        ref={input}
        id={id}
        className="krp-code__input"
        type="password"
        inputMode="none"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        data-lpignore="true"
        data-1p-ignore="true"
        maxLength={maxLength}
        value={value}
        disabled={off}
        aria-invalid={hasError || undefined}
        aria-describedby={`${hasError ? `${errorId} ` : ''}${hintId}`}
        onChange={(event) => change(event.target.value)}
      />
      {hasError && <p id={errorId} className="krp-code__error" role="alert"><ShieldAlert size={16} aria-hidden="true" /> <span>{error}</span></p>}
      <p id={hintId} className="krp-code__hint">{hint ?? `Введите код (от ${minLength} знаков: цифры и латинские буквы) или поднесите штрихкод к сканеру.`}</p>
      <div className="krp-code__pad" role="group" aria-label="Цифровая панель">
        {KEYS.map((key) => (
          <button key={key} type="button" className="krp-code__key" disabled={off} aria-label={`Цифра ${key}`} onMouseDown={(event) => event.preventDefault()} onClick={() => press(key)}>{key}</button>
        ))}
        <button type="button" className="krp-code__key" disabled={off || value === ''} aria-label="Стереть цифру" onMouseDown={(event) => event.preventDefault()} onClick={() => change(value.slice(0, -1))}><Delete size={20} aria-hidden="true" /></button>
        <button type="button" className="krp-code__key" disabled={off} aria-label="Цифра 0" onMouseDown={(event) => event.preventDefault()} onClick={() => press('0')}>0</button>
        <Button type="submit" variant="primary" {...(submitActionId === undefined ? {} : { actionId: submitActionId })} busy={busy} disabled={disabled || value.length < minLength} disabledReason={disabled ? null : `Введите код: не меньше ${minLength} цифр.`}>{submitLabel}</Button>
      </div>
    </form>
  )
}
